'use client';

import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface NotionPage {
  id: string;
  object: 'page';
  url: string;
  created_time: string;
  last_edited_time: string;
  properties: Record<string, any>;
  icon?: { type: string; emoji?: string; external?: { url: string } } | null;
  cover?: { type: string; external?: { url: string } } | null;
}

interface NotionDatabase {
  id: string;
  object: 'database';
  url: string;
  created_time: string;
  last_edited_time: string;
  title: Array<{ plain_text: string }>;
  icon?: { type: string; emoji?: string; external?: { url: string } } | null;
  cover?: { type: string; external?: { url: string } } | null;
}

type NotionResult = NotionPage | NotionDatabase;

interface SearchResponse {
  results: NotionResult[];
  has_more: boolean;
  next_cursor: string | null;
}

function getTitle(item: NotionResult): string {
  if (item.object === 'database') {
    return (item as NotionDatabase).title?.map((t) => t.plain_text).join('') || 'Untitled Database';
  }
  const page = item as NotionPage;
  const titleProp = Object.values(page.properties || {}).find(
    (p: any) => p.type === 'title'
  ) as any;
  return titleProp?.title?.map((t: any) => t.plain_text).join('') || 'Untitled Page';
}

function getIcon(item: NotionResult): string {
  if (item.icon?.type === 'emoji') return item.icon.emoji || '';
  return item.object === 'database' ? '🗄️' : '📄';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function NotionPage() {
  const [apiKey, setApiKey] = useState('');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<NotionResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'page' | 'database'>('all');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  async function fetchNotion(query: string, cursor?: string, append = false) {
    const body: Record<string, any> = {
      page_size: 20,
      sort: { direction: 'descending', timestamp: 'last_edited_time' },
    };
    if (query.trim()) body.query = query.trim();
    if (filter !== 'all') body.filter = { property: 'object', value: filter };
    if (cursor) body.start_cursor = cursor;

    const res = await fetch('https://api.notion.com/v1/search', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Notion-Version': '2022-06-28',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || `Error ${res.status}: ${res.statusText}`);
    }

    const data: SearchResponse = await res.json();
    setResults((prev) => (append ? [...prev, ...data.results] : data.results));
    setHasMore(data.has_more);
    setNextCursor(data.next_cursor);
  }

  async function handleConnect() {
    if (!apiKey.trim()) {
      setError('Please enter your Notion API key.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await fetchNotion('');
      setConnected(true);
    } catch (e: any) {
      setError(e.message || 'Failed to connect. Check your API key and try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch(q: string) {
    setSearchQuery(q);
    if (!connected) return;
    setLoading(true);
    setError('');
    try {
      await fetchNotion(q);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleFilterChange(f: 'all' | 'page' | 'database') {
    setFilter(f);
    if (!connected) return;
    setLoading(true);
    setError('');
    try {
      const body: Record<string, any> = {
        page_size: 20,
        sort: { direction: 'descending', timestamp: 'last_edited_time' },
      };
      if (searchQuery.trim()) body.query = searchQuery.trim();
      if (f !== 'all') body.filter = { property: 'object', value: f };

      const res = await fetch('https://api.notion.com/v1/search', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Notion-Version': '2022-06-28',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || `Error ${res.status}`);
      }
      const data: SearchResponse = await res.json();
      setResults(data.results);
      setHasMore(data.has_more);
      setNextCursor(data.next_cursor);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleLoadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      await fetchNotion(searchQuery, nextCursor, true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  }

  const pages = results.filter((r) => r.object === 'page');
  const databases = results.filter((r) => r.object === 'database');

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          {/* Page Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-4xl">📝</span>
              <h1 className="font-display text-4xl font-semibold text-foreground">
                Notion Workspace
              </h1>
            </div>
            <p className="text-foreground-soft text-base">
              Connect your Notion workspace to browse pages and databases.
            </p>
          </div>

          {/* Connection Card */}
          {!connected ? (
            <div className="bg-surface border border-border rounded-2xl p-8 max-w-lg">
              <h2 className="text-xl font-semibold text-foreground mb-1">Connect to Notion</h2>
              <p className="text-sm text-foreground-soft mb-6">
                Enter your Notion Internal Integration Token to get started. You can create one at{' '}
                <a
                  href="https://www.notion.so/my-integrations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  notion.so/my-integrations
                </a>
                .
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    API Key
                  </label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                    placeholder="secret_xxxxxxxxxxxxxxxxxxxx"
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-foreground-soft/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}
                <button
                  onClick={handleConnect}
                  disabled={loading}
                  className="w-full py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-foreground-soft transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? 'Connecting…' : 'Connect Workspace'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Bar */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { label: 'Total Items', value: results.length },
                  { label: 'Pages', value: pages.length },
                  { label: 'Databases', value: databases.length },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="bg-surface border border-border rounded-xl px-5 py-4 text-center"
                  >
                    <p className="text-2xl font-bold text-primary">{stat.value}</p>
                    <p className="text-xs text-foreground-soft mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Search + Filter */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search pages and databases…"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-surface text-foreground text-sm placeholder:text-foreground-soft/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                />
                <div className="flex gap-2">
                  {(['all', 'page', 'database'] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => handleFilterChange(f)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${
                        filter === f
                          ? 'bg-primary text-white' :'bg-surface border border-border text-foreground-soft hover:text-foreground'
                      }`}
                    >
                      {f === 'all' ? 'All' : f === 'page' ? 'Pages' : 'Databases'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
                  {error}
                </p>
              )}

              {/* Results */}
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="bg-surface border border-border rounded-xl p-5 animate-pulse"
                    >
                      <div className="h-8 w-8 bg-border rounded-lg mb-3" />
                      <div className="h-4 bg-border rounded w-3/4 mb-2" />
                      <div className="h-3 bg-border rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-16 text-foreground-soft">
                  <p className="text-5xl mb-4">🔍</p>
                  <p className="text-base font-medium">No results found</p>
                  <p className="text-sm mt-1">
                    Try a different search term or make sure pages are shared with your integration.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-surface border border-border rounded-xl p-5 hover:border-primary/50 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <span className="text-2xl leading-none mt-0.5">{getIcon(item)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                            {getTitle(item)}
                          </p>
                          <span
                            className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                              item.object === 'database' ?'bg-purple-100 text-purple-700' :'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {item.object === 'database' ? 'Database' : 'Page'}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-foreground-soft space-y-0.5">
                        <p>Created: {formatDate(item.created_time)}</p>
                        <p>Edited: {formatDate(item.last_edited_time)}</p>
                      </div>
                    </a>
                  ))}
                </div>
              )}

              {/* Load More */}
              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-8 py-2.5 bg-surface border border-border text-foreground text-sm font-medium rounded-xl hover:border-primary/50 transition-colors disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading…' : 'Load More'}
                  </button>
                </div>
              )}

              {/* Disconnect */}
              <div className="mt-10 pt-6 border-t border-border flex justify-end">
                <button
                  onClick={() => {
                    setConnected(false);
                    setApiKey('');
                    setResults([]);
                    setError('');
                    setSearchQuery('');
                    setFilter('all');
                  }}
                  className="text-sm text-foreground-soft hover:text-red-500 transition-colors"
                >
                  Disconnect Workspace
                </button>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
